import { usePersonnel } from 'features/Responder/features/Personnel';
import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import ScheduleCalendar from 'features/Responder/features/Schedules/features/ScheduleBuilder/components/RruleBuilder/components/ScheduleCalendar';
import {
  isRunOpen,
  resolvePersonnelName,
  resolveRunEnd,
  resolveRunSchedule,
  resolveRunScheduleColor,
  resolveRunScheduleId,
  resolveRunScheduleName,
  resolveRunStart,
  resolveRunUserId,
  resolveScheduleId,
  resolveSchedulePersonnelIds,
} from 'features/Responder/features/Schedules/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Stack } from 'react-bootstrap';
import { generateEventsBetween } from 'utils/recurrenceUtils';
import { RunItemCard } from './components/RunItemCard';
import { RunHistory } from './features/RunHistory';
import './runs-panel.css';

const parseDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

const parseMs = (value) => {
  const date = parseDate(value);
  return date ? date.getTime() : 0;
};

const formatDateTime = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleString() : 'Not scheduled';
};

const resolveScheduleDurationMs = (schedule) => {
  const explicit = Number(schedule?.duration);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const startMs = parseMs(schedule?.start_time);
  const endMs = parseMs(schedule?.end_time);
  if (startMs > 0 && endMs > startMs) return endMs - startMs;
  return 30 * 60 * 1000;
};

const getScheduleOccurrences = (schedule, rangeStart, rangeEnd) => {
  if (!schedule) return [];
  if (schedule?.recurrence) {
    try {
      const normalized = { ...schedule, duration: resolveScheduleDurationMs(schedule) };
      return generateEventsBetween(normalized, rangeStart, rangeEnd)
        .map((occurrence) => ({
          start: parseDate(occurrence.start),
          end: parseDate(occurrence.end),
        }))
        .filter((occurrence) => occurrence.start && occurrence.end)
        .sort((a, b) => a.start.getTime() - b.start.getTime());
    } catch (error) {
      console.warn('[RunsPanel] Unable to parse recurrence', error);
      return [];
    }
  }

  const start = parseDate(schedule?.start_time);
  if (!start) return [];
  const duration = resolveScheduleDurationMs(schedule);
  const end = new Date(start.getTime() + duration);
  if (end < rangeStart || start > rangeEnd) return [];
  return [{ start, end }];
};

export default function RunsPanel() {
  const {
    runs,
    schedules,
    colorMap,
    activeEditSchedule,
    setActiveEditSchedule,
    activeRunId,
    setActiveRunId,
    fetchRuns,
  } = useSchedules();
  const { personnel } = usePersonnel();
  const [activeTab, setActiveTab] = useState('active');
  const [historyView, setHistoryView] = useState('list');
  const [now, setNow] = useState(() => new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isHydratingCalendarEvents, setIsHydratingCalendarEvents] = useState(false);

  useEffect(() => {
    const timerId = window.setInterval(() => setNow(new Date()), 60 * 1000);
    return () => window.clearInterval(timerId);
  }, []);

  const scheduleById = useMemo(() => {
    const lookup = new Map();
    const addScheduleKey = (value, schedule) => {
      if (value == null || value === '') return;
      const key = `${value}`;
      if (!lookup.has(key)) lookup.set(key, schedule);
    };

    (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
      addScheduleKey(schedule?.id, schedule);
      addScheduleKey(resolveScheduleId(schedule), schedule);
      addScheduleKey(schedule?.definition?.id, schedule);
      addScheduleKey(schedule?.definitionId, schedule);
      addScheduleKey(schedule?.definition_id, schedule);
    });

    return lookup;
  }, [schedules]);

  const resolveRunScheduleDetails = useCallback(
    (run) => {
      const scheduleId = resolveRunScheduleId(run);
      const mappedSchedule = scheduleId != null ? scheduleById.get(`${scheduleId}`) || null : null;
      const embeddedSchedule = resolveRunSchedule(run);
      const schedule = mappedSchedule || embeddedSchedule || null;
      const scheduleName =
        schedule?.name ||
        resolveRunScheduleName(run) ||
        (scheduleId != null ? `Schedule #${scheduleId}` : 'Schedule #?');
      const scheduleColorKey = `${schedule?.color ?? resolveRunScheduleColor(run) ?? ''}`
        .trim()
        .toLowerCase();
      const palette = colorMap?.[scheduleColorKey] || colorMap?.blue || null;
      return { scheduleId, schedule, scheduleName, palette };
    },
    [colorMap, scheduleById],
  );

  const activeRuns = useMemo(
    () =>
      (Array.isArray(runs) ? runs : [])
        .filter((run) => isRunOpen(run))
        .sort((a, b) => parseMs(resolveRunStart(b)) - parseMs(resolveRunStart(a))),
    [runs],
  );

  const historicalRuns = useMemo(
    () =>
      (Array.isArray(runs) ? runs : [])
        .filter((run) => !isRunOpen(run))
        .sort((a, b) => {
          const aSort = parseMs(resolveRunEnd(a)) || parseMs(resolveRunStart(a));
          const bSort = parseMs(resolveRunEnd(b)) || parseMs(resolveRunStart(b));
          return bSort - aSort;
        }),
    [runs],
  );

  const activeRunBySchedule = useMemo(() => {
    const map = new Map();
    activeRuns.forEach((run) => {
      const scheduleId = resolveRunScheduleId(run);
      if (scheduleId == null || map.has(`${scheduleId}`)) return;
      map.set(`${scheduleId}`, run);
    });
    return map;
  }, [activeRuns]);

  const activeAndLateSchedules = useMemo(() => {
    const current = now.getTime();
    const rangeStart = new Date(current - 24 * 60 * 60 * 1000);
    const rangeEnd = new Date(current + 24 * 60 * 60 * 1000);
    const late = [];

    (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
      const occurrences = getScheduleOccurrences(schedule, rangeStart, rangeEnd);
      const activeOccurrence = occurrences.find((occ) => {
        const startMs = occ.start?.getTime?.() ?? 0;
        const endMs = occ.end?.getTime?.() ?? 0;
        return startMs <= current && endMs >= current;
      });
      if (!activeOccurrence) return;
      if (activeRunBySchedule.has(`${schedule.id}`)) return;
      late.push({
        schedule,
        occurrence: activeOccurrence,
      });
    });

    return late.sort((a, b) => {
      const aStart = a.occurrence?.start?.getTime?.() ?? 0;
      const bStart = b.occurrence?.start?.getTime?.() ?? 0;
      return aStart - bStart;
    });
  }, [activeRunBySchedule, now, schedules]);

  const upcomingSchedules = useMemo(() => {
    const current = now.getTime();
    const rangeStart = new Date(current);
    const rangeEnd = new Date(current + 24 * 60 * 60 * 1000);
    const used = new Set();
    const upcoming = [];

    (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
      const scheduleKey = `${schedule?.id ?? ''}`;
      if (!scheduleKey) return;
      if (used.has(scheduleKey)) return;

      const occurrences = getScheduleOccurrences(schedule, rangeStart, rangeEnd);
      const nextOccurrence = occurrences.find((occ) => {
        const startMs = occ.start?.getTime?.() ?? 0;
        return startMs > current;
      });
      if (!nextOccurrence) return;
      used.add(scheduleKey);
      upcoming.push({ schedule, occurrence: nextOccurrence });
    });

    return upcoming.sort(
      (a, b) =>
        (a.occurrence?.start?.getTime?.() ?? Number.MAX_SAFE_INTEGER) -
        (b.occurrence?.start?.getTime?.() ?? Number.MAX_SAFE_INTEGER),
    );
  }, [now, schedules]);

  const personnelLabelForSchedule = useMemo(
    () =>
      (schedule, run = null) => {
        const runUserId = resolveRunUserId(run);
        if (runUserId != null) {
          const name = resolvePersonnelName(personnel, runUserId);
          if (name) return name;
          return `User #${runUserId}`;
        }
        const ids = resolveSchedulePersonnelIds(schedule);
        if (!ids.length) return 'Unassigned';
        const names = ids
          .map((id) => resolvePersonnelName(personnel, id) || `User #${id}`)
          .filter(Boolean);
        return names.join(', ') || 'Unassigned';
      },
    [personnel],
  );

  const buildRunCalendarEvent = useCallback(
    (run) => {
      const { scheduleId, schedule, scheduleName, palette } = resolveRunScheduleDetails(run);
      const start = parseDate(resolveRunStart(run));
      if (!start) return null;

      const end =
        parseDate(resolveRunEnd(run)) ||
        new Date(start.getTime() + resolveScheduleDurationMs(schedule));

      return {
        id: `run-${run?.id ?? `${scheduleId ?? 'na'}-${start.getTime()}`}`,
        run,
        schedule,
        start,
        end,
        colors: palette
          ? {
              bgColor: palette?.bgColor || '#436c9a',
              textColor: palette?.textColor || '#ffffff',
              barColor: palette?.barColor || '#1e374e',
            }
          : undefined,
        title: `${scheduleName} - ${personnelLabelForSchedule(schedule, run)}`,
      };
    },
    [personnelLabelForSchedule, resolveRunScheduleDetails],
  );

  useEffect(() => {
    if (activeTab !== 'history' || historyView !== 'calendar') {
      setIsHydratingCalendarEvents(false);
      return undefined;
    }

    const sourceRuns = Array.isArray(historicalRuns) ? historicalRuns : [];
    if (!sourceRuns.length) {
      setCalendarEvents([]);
      setIsHydratingCalendarEvents(false);
      return undefined;
    }

    let isActive = true;
    let timeoutId = null;
    const nextEvents = [];
    const chunkSize = 40;
    let index = 0;
    const hydrateStartedAt = Date.now();

    setIsHydratingCalendarEvents(true);

    const finalizeHydration = () => {
      if (!isActive) return;
      setCalendarEvents(nextEvents);
      setIsHydratingCalendarEvents(false);
    };

    const buildChunk = () => {
      if (!isActive) return;

      const endIndex = Math.min(index + chunkSize, sourceRuns.length);
      for (; index < endIndex; index += 1) {
        const event = buildRunCalendarEvent(sourceRuns[index]);
        if (event) nextEvents.push(event);
      }

      if (index < sourceRuns.length) {
        timeoutId = window.setTimeout(buildChunk, 0);
        return;
      }

      const minHydrationMs = 180;
      const elapsedMs = Date.now() - hydrateStartedAt;
      if (elapsedMs < minHydrationMs) {
        timeoutId = window.setTimeout(finalizeHydration, minHydrationMs - elapsedMs);
        return;
      }

      finalizeHydration();
    };

    timeoutId = window.setTimeout(buildChunk, 0);

    return () => {
      isActive = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [activeTab, buildRunCalendarEvent, historicalRuns, historyView]);

  const handleSelectRun = (run) => {
    if (!run) return;
    const { schedule } = resolveRunScheduleDetails(run);
    if (schedule) setActiveEditSchedule(schedule);
    setActiveRunId?.(run.id ?? null);
  };

  const handleSelectSchedule = (schedule) => {
    if (!schedule) return;
    setActiveEditSchedule(schedule);
    setActiveRunId?.(null);
  };

  const activeCount = activeRuns.length + activeAndLateSchedules.length;
  const selectedScheduleId = activeEditSchedule?.id;

  return (
    <Card className="runs-panel-card shadow-sm h-100 bg-dark text-white border-secondary">
      <Card.Body className="runs-panel-body d-flex flex-column h-100 gap-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted text-uppercase small">Activities</div>
            <div className="fw-semibold fs-5">Activity</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={activeCount ? 'primary' : 'secondary'}>{activeCount} active</Badge>
            <Button size="sm" variant="outline-primary" onClick={() => fetchRuns?.()}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant={activeTab === 'active' ? 'primary' : 'outline-light'}
            onClick={() => setActiveTab('active')}
            className={`px-3 runs-tab-btn ${activeTab === 'active' ? 'runs-tab-btn--active' : ''}`}
          >
            Active
          </Button>
          <Button
            size="sm"
            variant={activeTab === 'history' ? 'primary' : 'outline-light'}
            onClick={() => setActiveTab('history')}
            className={`px-3 runs-tab-btn ${activeTab === 'history' ? 'runs-tab-btn--active' : ''}`}
          >
            History
          </Button>
        </div>

        {activeTab === 'active' && (
          <div className="runs-panel-scroll d-flex flex-column gap-3 flex-grow-1">
            <section className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Active</div>
                <Badge bg="secondary">{activeCount}</Badge>
              </div>
              {activeCount ? (
                <Stack gap={2}>
                  {activeRuns.map((run) => {
                    const { schedule, scheduleName } = resolveRunScheduleDetails(run);
                    const status = `${run?.status ?? run?.state ?? 'active'}`.replace(
                      /[_-]+/g,
                      ' ',
                    );
                    return (
                      <RunItemCard
                        key={`active-run-${run.id}`}
                        title={scheduleName}
                        subtitle={personnelLabelForSchedule(schedule, run)}
                        detail={`Started: ${formatDateTime(resolveRunStart(run))}`}
                        statusLabel={status}
                        statusVariant="success"
                        meta={run?.id != null ? `Activity #${run.id}` : ''}
                        isSelected={`${activeRunId ?? ''}` === `${run?.id ?? ''}`}
                        onClick={() => handleSelectRun(run)}
                      />
                    );
                  })}
                  {activeAndLateSchedules.map(({ schedule, occurrence }) => (
                    <RunItemCard
                      key={`late-schedule-${schedule.id}`}
                      title={schedule?.name || `Schedule #${schedule?.id ?? '?'}`}
                      subtitle={personnelLabelForSchedule(schedule)}
                      detail={`Expected start: ${formatDateTime(occurrence?.start)}`}
                      statusLabel="Late"
                      statusVariant="danger"
                      meta="No active activity started"
                      isSelected={
                        !activeRunId && `${selectedScheduleId ?? ''}` === `${schedule?.id ?? ''}`
                      }
                      onClick={() => handleSelectSchedule(schedule)}
                    />
                  ))}
                </Stack>
              ) : (
                <div className="text-muted small">No active or late activities.</div>
              )}
            </section>

            <section className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Upcoming (24hrs)</div>
                <Badge bg="secondary">{upcomingSchedules.length}</Badge>
              </div>
              {upcomingSchedules.length ? (
                <Stack gap={2}>
                  {upcomingSchedules.map(({ schedule, occurrence }) => (
                    <RunItemCard
                      key={`upcoming-schedule-${schedule?.id}-${occurrence?.start?.getTime?.() ?? 'na'}`}
                      title={schedule?.name || `Schedule #${schedule?.id ?? '?'}`}
                      subtitle={personnelLabelForSchedule(schedule)}
                      detail={`Starts: ${formatDateTime(occurrence?.start)}`}
                      statusLabel="Upcoming"
                      statusVariant="info"
                      isSelected={
                        !activeRunId && `${selectedScheduleId ?? ''}` === `${schedule?.id ?? ''}`
                      }
                      onClick={() => handleSelectSchedule(schedule)}
                    />
                  ))}
                </Stack>
              ) : (
                <div className="text-muted small">No activities scheduled in the next 24 hours.</div>
              )}
            </section>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="runs-panel-scroll d-flex flex-column flex-grow-1 gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={historyView === 'list' ? 'primary' : 'outline-light'}
                onClick={() => setHistoryView('list')}
                className={`px-3 runs-tab-btn ${historyView === 'list' ? 'runs-tab-btn--active' : ''}`}
              >
                List
              </Button>
              <Button
                size="sm"
                variant={historyView === 'calendar' ? 'primary' : 'outline-light'}
                onClick={() => setHistoryView('calendar')}
                className={`px-3 runs-tab-btn ${historyView === 'calendar' ? 'runs-tab-btn--active' : ''}`}
              >
                Calendar
              </Button>
            </div>

            {historyView === 'list' ? (
              <div className="flex-grow-1" style={{ minHeight: 0 }}>
                <RunHistory
                  statusFilter="history"
                  showScheduleColumn
                  enableRunSelection
                  useParentScroll
                />
              </div>
            ) : (
              <div className="runs-history-calendar-wrap flex-grow-1" style={{ minHeight: 0 }}>
                <ScheduleCalendar
                  events={calendarEvents}
                  showToolbar={false}
                  showDaily
                  showAgenda
                  isHydratingEvents={isHydratingCalendarEvents}
                  onSelectEvent={(event) => handleSelectRun(event?.run)}
                  height={0}
                />
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
